import axios from "axios";
import _ from "lodash";

export const moduleNamespace = 'stripeConnectDashboard';
import {moduleNamespace as productManagerModuleNamespace} from './productManager/constants';
import {moduleNamespace as addonManagerModuleNamespace} from './addonManager/constants';

const FETCH_COMPANY_INFO = 'FETCH_COMPANY_INFO';
const FETCH_REGISTRATION_LINKS = 'FETCH_REGISTRATION_LINKS';
const CLOSE_MODALS = 'CLOSE_MODALS';
const EDIT_PRODUCT = 'EDIT_PRODUCT';
const MANAGE_ADDONS = 'MANAGE_ADDONS';
const UPDATE_ADDONS = 'UPDATE_ADDONS';

const SOFTWARE_PRODUCT_CONTENT_FALLBACKS = {
  name: 'SEO Software',
};

/**
 * Used to parse price objects from the backend and format their values to strings.
 */
function formatPriceObject(supportedCurrencies, pricesByCurrency) {
  let formattedPriceObjects = {};

  _.forEach(pricesByCurrency, (prices, currency) => {
    let monthlyRaw = 0;
    let annualRaw = 0;

    _.forEach(prices, (price) => {
      if (price.billing_period === 'MONTH') {
        monthlyRaw = price.price / 100;
      }
      if (price.billing_period === 'YEAR') {
        annualRaw = price.price / 100;
      }
    });

    formattedPriceObjects[currency] = {
      monthlyRaw: monthlyRaw * 100,
      annualRaw: annualRaw * 100,
    };
  });

  Object.entries(supportedCurrencies).forEach(([currencyCode]) => {
    if (!formattedPriceObjects[currencyCode]) {
      formattedPriceObjects[currencyCode] = {
        monthlyRaw: 0,
        annualRaw: 0,
      };
    }
  });

  return formattedPriceObjects;
}

/**
 * Used to provide a default dataset for a given statbox.
 */
function defaultStatBox() {
  return {
    value: 0,
    diff: 0,
    data: {},
    range: [],
    rangeLimits: {
      start: '',
      end: '',
    }
  };
}

const defaultState = () => ({
  companyInfo: {
    totalCustomers: defaultStatBox(),
    newCustomers: defaultStatBox(),
    lostCustomers: defaultStatBox(),
  },
  registrationLinks: [],
  showProductModal: false,
  showAddonsModal: false,
});

const getters = {
  companyInfo: ({companyInfo}) => companyInfo,
  companyName: ({companyInfo}) => companyInfo?.companyName,
  products: ({companyInfo}) => {
    return companyInfo.softwareProducts ? companyInfo.softwareProducts.map((product) => {
      return {
        ...product,
        fallbackName: SOFTWARE_PRODUCT_CONTENT_FALLBACKS.name,
      };
    }) : {};
  },
  addons: ({companyInfo}) => companyInfo?.addons,
  onlyRegistrationLink: ({registrationLinks}) => registrationLinks[0],
  registrationLinks: ({registrationLinks}) => registrationLinks,
  showProductModal: ({showProductModal}) => showProductModal,
  showAddonsModal: ({showAddonsModal}) => showAddonsModal,
  statBoxCount: ({companyInfo}) => (property) => companyInfo[property].value,
  statBoxDiffAmount: ({companyInfo}) => (property) => companyInfo[property].diff,
  statBoxDiff: ({companyInfo}) => (property) => Math.sign(companyInfo[property].diff),
  statBoxRange: ({companyInfo}) => (property) => companyInfo[property].range,
  statBoxRangeLimits: ({companyInfo}) => (property) => {
    return {
      start: companyInfo[property].rangeLimits[0],
      end: companyInfo[property].rangeLimits[1],
    };
  },
};

const actions = {
  fetchCompanyInfo: ({commit}) => commit(FETCH_COMPANY_INFO),
  fetchRegistrationLinks: ({commit}) => commit(FETCH_REGISTRATION_LINKS),
  closeModals: ({commit}) => commit(CLOSE_MODALS),
  editProduct: ({commit, rootState}, softwareProduct) => commit(EDIT_PRODUCT, {rootState, softwareProduct}),
  manageAddons: ({commit, rootState, rootGetters}, payload) => commit(MANAGE_ADDONS, {rootState, rootGetters, payload}),
};

const mutations = {
  [FETCH_COMPANY_INFO](state) {
    axios.get('/merchant/company-info').then(({ data }) => {
      state.companyInfo = data;
    });
  },
  [FETCH_REGISTRATION_LINKS](state) {
    axios.get('/merchant/registration-links').then(({ data }) => {
      const { links } = data;
      state.registrationLinks = links;
    });
  },
  [CLOSE_MODALS](state) {
    state.showProductModal = false;
    state.showAddonsModal = false;
  },
  [EDIT_PRODUCT](state, {rootState, softwareProduct}) {
    /**
     * Separating out the nested support addon.
     */
    const {supportAddon, ...softwareProductOnly} = softwareProduct;

    const {supportedCurrencies} = rootState[productManagerModuleNamespace];

    /**
     * We're looking to edit an existing product (and support addon), which means we're working with a slightly
     * different pricing structure than the EditProductForm expects. So we'll need to reshape it a little.
     */
    const {prices: softwarePricesByCurrency} = softwareProductOnly;
    let softwarePrices = formatPriceObject(
      supportedCurrencies,
      softwarePricesByCurrency,
    );

    Object.entries(supportedCurrencies)
      .forEach(([currencyCode]) => {
        if (
          softwarePrices[currencyCode] &&
          (
            softwarePrices[currencyCode].monthlyRaw > 0 ||
            softwarePrices[currencyCode].annualRaw > 0
          )
        ) {
          rootState[productManagerModuleNamespace].supportedCurrencies[currencyCode] = {
            ...supportedCurrencies[currencyCode],
            enabled: true,
          };
        }
      });

    /**
     * We'll write to the product manager directly, as that is what EditProductForm relies on.
     */
    rootState[productManagerModuleNamespace].softwareProduct = {
      ...softwareProductOnly,
      name: softwareProductOnly.display_name,
      prices: softwarePrices,
    };

    if (supportAddon) {
      const {prices: supportAddonPricesByCurrency} = supportAddon;
      let supportAddonPrices = formatPriceObject(
        supportedCurrencies,
        supportAddonPricesByCurrency,
      );
      rootState[productManagerModuleNamespace].enableSupportAddon = true;
      rootState[productManagerModuleNamespace].supportAddon = {
        ...supportAddon,
        name: supportAddon.display_name,
        prices: supportAddonPrices,
      };
    }

    state.showProductModal = true;
  },
  [MANAGE_ADDONS](state, {rootState, payload}) {
    const {addons, products} = payload;

    /**
     * Separating out the nested support addon.
     */
    const {supportAddon, ...softwareProductOnly} = products[0];

    const {supportedCurrencies} = rootState[productManagerModuleNamespace];

    /**
     * We're looking to edit an existing product (and support addon), which means we're working with a slightly
     * different pricing structure than the EditProductForm expects. So we'll need to reshape it a little.
     */
    const {prices: softwarePricesByCurrency} = softwareProductOnly;
    let softwarePrices = formatPriceObject(supportedCurrencies, softwarePricesByCurrency);

    Object.entries(supportedCurrencies)
      .forEach(([currencyCode]) => {
        if (
          softwarePrices[currencyCode] &&
          (
            softwarePrices[currencyCode].monthlyRaw > 0 ||
            softwarePrices[currencyCode].annualRaw > 0
          )
        ) {
          rootState[productManagerModuleNamespace].supportedCurrencies[currencyCode] = {
            ...supportedCurrencies[currencyCode],
            enabled: true,
          };
        }
      });

    /**
     * We'll write to the product manager directly, as that is what EditProductForm relies on.
     */
    rootState[productManagerModuleNamespace].softwareProduct = {
      ...softwareProductOnly,
      name: softwareProductOnly.display_name,
      prices: softwarePrices,
    };

    /**
     * We need to take the addons data from the readable/structured format and boil it down to meet the expectations
     * of the ManageAddonsForm component.
     *
     * First we reduce the addon currency groupings into a flat array of prices.
     */
    const justPrices = addons.reduce((prices, addon) => {
      return [
        ...prices,
        ...addon.prices.GBP ?? [],
        ...addon.prices.USD ?? [],
      ];
    }, []);

    /**
     * Then use a reduce to key-map the flat prices array to an array of strings keyed by the original
     * resellable addon stripe_id.
     */
    rootState[addonManagerModuleNamespace].addons = justPrices.reduce(
      (keyedPrices, {original_stripe_id: originalStripeId, price}) => {
        keyedPrices[originalStripeId] = price;
        return keyedPrices;
      }, {}
    );
    state.showAddonsModal = true;
  },
};

export default {
  namespaced: true,
  state: defaultState(),
  getters,
  actions,
  mutations,
};
